import { useQuery } from '@tanstack/react-query';
import { FC } from 'react';
import styled, { css } from 'styled-components';

import Text from '@/components/Text';
import { api } from '@/utils/api';

const Box = styled.button`
  user-select: none;
  display: flex;
  padding: 4px;
  padding-left: 12px;
  border-radius: 4px;
  gap: 6px;
  border: solid 1px ${({ theme }) => theme.colors.lightGray};
  background-color: ${({ theme }) => theme.colors.white};
  align-items: center;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: rgba(34, 34, 34, 0.25);
  }
`;

const Switches = styled.div<{ $inactive?: boolean; $active?: boolean }>`
  display: flex;
  position: relative;
  gap: 4px;
  &:after {
    content: '';
    width: 46px;
    height: 24px;
    border-radius: 4px;
    position: absolute;
    left: 0;
    top: 0;
    transition: all ease-out 0.2s;
  }
  ${({ $inactive }) =>
    $inactive &&
    css`
      &:after {
        background-color: ${({ theme }) => theme.colors.lightGray};
      }
    `}
  ${({ $active }) =>
    $active &&
    css`
      color: ${({ theme }) => theme.colors.white};
      &:after {
        left: 50px;
        background-color: ${({ theme }) => theme.colors.purple};
      }
    `}
`;

const Switch = styled(Text)<{ $active?: boolean }>`
  width: 46px;
  height: 24px;
  justify-content: center;
  align-items: center;
  display: flex;
  color: ${({ theme }) => theme.colors.gray};
  position: relative;
  transition: color ease-out 0.2s;
  span {
    position: relative;
    z-index: 1;
  }
  ${({ $active }) =>
    $active &&
    css`
      color: ${({ theme }) => theme.colors.white};
    `}
`;

const NumberBadge = styled(Text)<{ $isActive: boolean }>`
  background-color: ${({ theme }) => theme.colors.error};
  height: 18px;
  width: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 9px;
  margin-right: 6px;
  ${({ $isActive }) =>
    $isActive &&
    css`
      background-color: ${({ theme }) => theme.colors.gray};
    `}
`;

interface Props {
  isActive: boolean;
  onClick: () => void;
}

const FreelancersSwitch: FC<Props> = ({ isActive, onClick }) => {
  const { data, isLoading } = useQuery<{
    count: number;
  }>(
    ['freelancers_count'],
    () =>
      api.get<
        never,
        {
          count: number;
        }
      >('/v1/payments/freelancers-ending-contracts-count'),
    {
      staleTime: 120 * 10,
    },
  );

  return (
    <Box onClick={onClick}>
      <Text $type="smallHeadline">Expiring contracts:</Text>
      <NumberBadge
        $color="white"
        $type="extraSmall"
        $isActive={isActive || isLoading || !data?.count}
      >
        {isLoading ? '-' : data?.count}
      </NumberBadge>
      <Switches $inactive={!isActive} $active={isActive}>
        <Switch $type="smallHeadline">
          <span>Hide</span>
        </Switch>
        <Switch $active={isActive} $type="smallHeadline">
          <span>Show</span>
        </Switch>
      </Switches>
    </Box>
  );
};

export default FreelancersSwitch;
